import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { RichText } from '../i18n/RichText';
import { t } from '../services/language-manager';
import { isAiReply, isConversationInfo, isSendFiles, needsWebhook } from '../services/action-types';
import { ACTIONS_GUIDE_URL } from '../config/wizard-config';
import { ActionTypePicker } from './ActionTypePicker';
import { enableRequirements } from './ActionReadiness';
import { discardButtonProps } from './CustomFieldsEditor';
import { BilledIcon } from './BilledMark';

interface AddActionDialogProps {
  /** Types the org may create (GET /api/org-actions `allowedTypes`). */
  allowedTypes: string[];
  /** Types whose use is charged to the org (GET /api/org-actions `billedTypes`). */
  billedTypes: string[];
  /** Picker input id; the section's failed-save focus targets it (`action-type-<key>`). */
  typeInputId: string;
  type: string;
  onTypeChange: (type: string) => void;
  /** Display label typed so far, named in the Create step. */
  label: string;
  invalidType: boolean;
  /** The action settings form, shown once a type is picked. */
  children?: ReactNode;
  /** First problem that keeps Create disabled (the same check as Save), or null. */
  blocker: string | null;
  /** Failed create, or a server problem with what was entered. */
  error: string | null;
  /** Id of the feedback line; the form's invalid inputs point at it. */
  feedbackId: string;
  saving: boolean;
  /** The webhook settings typed here passed a test in this dialog (cleared when they change). */
  webhookTested: boolean;
  /** Conversation info: at least one field has been added. */
  hasInfoFields?: boolean;
  /** Conversation info: the clash with another enabled info action if this one were created enabled, or null. */
  enableConflict?: string | null;
  /** Cancel / Escape / backdrop. The section asks before discarding input. */
  onCancel: () => void;
  /** Saves with the chosen state (disabled while something required is outstanding); resolves true once the action exists. */
  onCreate: (enabled: boolean) => Promise<boolean>;
}

const TABBABLE = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/**
 * Elements Tab can reach, in order. A radio group is one stop: its checked
 * radio, or the first one while none is checked (what the browser does).
 */
function tabbables(root: HTMLElement): HTMLElement[] {
  const seenGroups = new Set<string>();
  return Array.from(root.querySelectorAll<HTMLElement>(TABBABLE)).flatMap((el) => {
    if (!(el instanceof HTMLInputElement) || el.type !== 'radio' || !el.name) return [el];
    if (seenGroups.has(el.name)) return [];
    seenGroups.add(el.name);
    const checked = root.querySelector<HTMLInputElement>(`input[type="radio"][name="${el.name}"]:checked`);
    return [checked ?? el];
  });
}

/** Keeps Tab and Shift+Tab inside `root`; returns true when it moved the focus. */
function trapTab(e: KeyboardEvent<HTMLElement>, root: HTMLElement | null): boolean {
  if (e.key !== 'Tab' || !root) return false;
  const items = tabbables(root);
  if (items.length === 0) return false;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement;
  const outside = !root.contains(active);
  if (e.shiftKey && (active === first || outside)) {
    e.preventDefault();
    last.focus();
    return true;
  }
  if (!e.shiftKey && (active === last || outside)) {
    e.preventDefault();
    first.focus();
    return true;
  }
  return false;
}

/**
 * Add action: one dialog from the type to a created action. The admin picks
 * a type, fills in its settings below the cards, and Create confirms: AI Reply
 * asks enabled or disabled, and so does a webhook type with nothing required
 * outstanding (Send Files after a successful test here). Otherwise it is
 * created disabled and the step lists what is still to do before it can be
 * switched on: the test, and for orders and returns the catalog, which needs
 * the saved action. Its card then says what is still needed. Nothing is added to
 * the list before that.
 * The title and footer stay put while the body scrolls.
 */
export function AddActionDialog({
  allowedTypes,
  billedTypes,
  typeInputId,
  type,
  onTypeChange,
  label,
  invalidType,
  children,
  blocker,
  error,
  feedbackId,
  saving,
  webhookTested,
  hasInfoFields = false,
  enableConflict = null,
  onCancel,
  onCreate,
}: AddActionDialogProps) {
  const [choosing, setChoosing] = useState(false);
  const [creatingEnabled, setCreatingEnabled] = useState<boolean | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const createRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const canCreate = Boolean(type) && !blocker && !saving;
  const blockerId = `${feedbackId}-blocker`;

  useEffect(() => {
    const root = dialogRef.current;
    if (root) tabbables(root)[0]?.focus();
  }, []);

  useEffect(() => {
    // Back first: Enter must not create in a state the admin did not pick.
    if (choosing) backRef.current?.focus();
  }, [choosing]);

  // Create is disabled while saving, so returning to it waits for that render.
  const [refocusCreate, setRefocusCreate] = useState(false);
  useEffect(() => {
    if (!refocusCreate || choosing || saving) return;
    setRefocusCreate(false);
    createRef.current?.focus();
  }, [refocusCreate, choosing, saving]);

  const closeConfirm = () => {
    setChoosing(false);
    setRefocusCreate(true);
  };

  const create = async (enabled: boolean) => {
    setCreatingEnabled(enabled);
    const created = await onCreate(enabled);
    setCreatingEnabled(null);
    // On success the section closes this dialog; on failure the feedback line
    // says why (and a pointed-at input takes the focus right after).
    if (!created) closeConfirm();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!saving) onCancel();
      return;
    }
    trapTab(e, dialogRef.current);
  };

  const handleConfirmKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (!saving) closeConfirm();
      return;
    }
    trapTab(e, confirmRef.current);
  };

  const webhookType = Boolean(type) && needsWebhook(type);
  const outstanding = enableRequirements(type, { webhookTested, catalogLoaded: false, hasInfoFields }).filter((r) => !r.done);
  const forcedOff = outstanding.length > 0 || Boolean(enableConflict);
  const billed = Boolean(type) && billedTypes.includes(type);

  const typeHint = !type
    ? null
    : isSendFiles(type)
      ? 'settings-actions-type-fixed-hint-files'
      : isAiReply(type)
        ? 'settings-actions-type-fixed-hint-reply'
        : isConversationInfo(type) ? 'settings-actions-type-fixed-hint-info' : 'settings-actions-type-both-hint';
  const todoKey = (key: string) => (key === 'webhook'
    ? 'settings-actions-create-todo-webhook'
    : key === 'fields' ? 'settings-actions-create-todo-fields' : 'settings-actions-create-todo-catalog');

  return (
    <>
      <div
        className="confirm-dialog__backdrop add-action-dialog__backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving) onCancel();
        }}
      >
        <div
          ref={dialogRef}
          className="confirm-dialog add-action-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-action-dialog-title"
          aria-describedby="add-action-dialog-message"
          onKeyDown={handleKeyDown}
          inert={choosing}
        >
          <h3 id="add-action-dialog-title" className="confirm-dialog__title">{t('settings-actions-add-dialog-title')}</h3>
          <p id="add-action-dialog-message" className="confirm-dialog__message">{t('settings-actions-type-choose-hint')}</p>
          <div className="add-action-dialog__body">
            {/* No preselection: the admin picks a type deliberately. */}
            <ActionTypePicker
              id={typeInputId}
              allowedTypes={allowedTypes}
              billedTypes={billedTypes}
              value={type}
              onChange={onTypeChange}
              invalid={invalidType}
              describedBy={feedbackId}
            />
            {typeHint && <p className="form-hint add-action-dialog__type-hint">{t(typeHint)}</p>}
            {type && <div className="add-action-dialog__form">{children}</div>}
          </div>
          {/* Explanation and the guide on the left, reading left to right; buttons on the right. */}
          <div className="add-action-dialog__footer">
            <div className="add-action-dialog__footer-text">
              <div id={feedbackId}>
                {error ? (
                  <p className="save-row__message save-row__message--error" role="alert">{error}</p>
                ) : type && blocker ? (
                  // Styled as an error; the input it names is marked invalid in the form.
                  <p className="save-row__message save-row__message--error" id={blockerId}>{blocker}</p>
                ) : null}
              </div>
              {/* One guide covers every type, so it stays here on both steps. */}
              <p className="add-action-dialog__guide">
                <RichText
                  k="settings-actions-guide"
                  values={{
                    guide: (
                      <a
                        href={ACTIONS_GUIDE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={t('settings-actions-guide-link-label')}
                      >
                        {t('settings-actions-guide-link')}
                      </a>
                    ),
                  }}
                />
              </p>
            </div>
            <div className="add-action-dialog__buttons">
              <button type="button" className="btn btn--secondary" {...discardButtonProps} onClick={onCancel} disabled={saving}>
                {t('confirm-dialog-cancel')}
              </button>
              <button
                ref={createRef}
                type="button"
                className="btn btn--primary"
                disabled={!canCreate}
                aria-describedby={type && blocker && !error ? blockerId : undefined}
                aria-haspopup="dialog"
                onClick={() => setChoosing(true)}
              >
                {t('settings-actions-add-dialog-create')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {choosing && (
        <div
          className="confirm-dialog__backdrop add-action-dialog__confirm-backdrop"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget && !saving) closeConfirm();
          }}
        >
          <div
            ref={confirmRef}
            className="confirm-dialog add-action-dialog__confirm"
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-action-confirm-title"
            aria-describedby="add-action-confirm-message"
            onKeyDown={handleConfirmKeyDown}
          >
            <h3 id="add-action-confirm-title" className="confirm-dialog__title">{t('settings-actions-create-confirm-title')}</h3>
            {label.trim() && (
              <p className="confirm-dialog__subject">
                <bdi>{label.trim()}</bdi>
              </p>
            )}
            <div id="add-action-confirm-message">
              {billed && (
                <p className="add-action-dialog__billed">
                  <BilledIcon />
                  <span>{t('settings-actions-create-confirm-billed')}</span>
                </p>
              )}
              <p className="confirm-dialog__message">
                {t(forcedOff
                  ? 'settings-actions-create-confirm-message-webhook'
                  : webhookType ? 'settings-actions-create-confirm-message-tested' : 'settings-actions-create-confirm-message-reply')}
              </p>
              {/* Plain to-do text: nothing here is a choice, so no checkbox or radio look. */}
              {forcedOff && (
                <ul className="add-action-dialog__todo">
                  {outstanding.map((r) => (
                    <li key={r.key}>{t(todoKey(r.key))}</li>
                  ))}
                  {enableConflict && <li key="conflict">{enableConflict} {t('settings-actions-info-conflict-fix')}</li>}
                </ul>
              )}
            </div>
            <div className="confirm-dialog__actions">
              <button
                ref={backRef}
                type="button"
                className="btn btn--secondary"
                onClick={() => closeConfirm()}
                disabled={saving}
              >
                {t('settings-actions-create-confirm-back')}
              </button>
              <button
                type="button"
                className={`btn ${forcedOff ? 'btn--primary' : 'btn--secondary'}`}
                onClick={() => void create(false)}
                disabled={saving}
                aria-busy={creatingEnabled === false}
              >
                {creatingEnabled === false && <span className="btn-spinner" aria-hidden="true" />}
                {t('settings-actions-create-disabled')}
              </button>
              {!forcedOff && (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void create(true)}
                  disabled={saving}
                  aria-busy={creatingEnabled === true}
                >
                  {creatingEnabled === true && <span className="btn-spinner" aria-hidden="true" />}
                  {t('settings-actions-create-enable')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
